import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { useAuth } from "../../contexts/auth";
import { baseAxios, Context } from "../../utils/config";
import { colors } from "../../theme/colors";
import { useUnsavedWork } from "../../contexts/unsavedWork";
import {
  clearReceivingDraft,
  draftContentKey,
  formatDraftTime,
  hasDraftContent,
  loadReceivingDraft,
  saveReceivingDraft,
} from "../../utils/receivingDraft";
import {
  catalogDetailForRow,
  isNewManufacturer,
  manufacturerPickerRows,
  namesMatch,
} from "./receivingCatalog";
import {
  optionLabel,
  withAddOption,
} from "../ManageProducts/productFormHelpers";

const CONTROL_RADIUS = "6px";

const filterManufacturers = createFilterOptions({
  stringify: (option) => String(option?.manufacturer_name ?? ""),
});

/** Picker filter plus an `Add "text"` option for a name no row has (JD-464). */
function manufacturerFilterOptions(options, state) {
  return withAddOption(filterManufacturers(options, state), state.inputValue, {
    key: "manufacturer_name",
    all: options,
  });
}

function sortedManufacturers(rows) {
  return [...(rows || [])]
    .map((row) => ({
      ...row,
      manufacturer_name: String(row.manufacturer_name ?? "").trim(),
    }))
    .filter((row) => row.manufacturer_name)
    .sort((a, b) =>
      a.manufacturer_name.localeCompare(b.manufacturer_name, undefined, {
        sensitivity: "base",
      })
    );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "background.paper",
    borderRadius: CONTROL_RADIUS,
  },
};

const compactFieldSx = {
  width: "100%",
  maxWidth: 112,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: CONTROL_RADIUS,
    fontWeight: 600,
    fontSize: 13,
    height: 28,
    minWidth: 0,
  },
  "& .MuiOutlinedInput-input": {
    py: 0.25,
    px: 0.75,
    minWidth: 0,
  },
};

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return (Math.round(n * 100) / 100).toFixed(2);
}

function textCell(value) {
  return (
    <Typography
      component="span"
      fontSize={12}
      sx={{ overflowWrap: "break-word", lineHeight: 1.3 }}
    >
      {value || "—"}
    </Typography>
  );
}

function ResumeDraftToast({ savedAt, onDiscard, onDismiss }) {
  const when = formatDraftTime(savedAt);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: "#333",
        color: "#fff",
        px: 2,
        py: 1.5,
        borderRadius: 2,
        boxShadow: 3,
        minWidth: 320,
      }}
    >
      <Typography variant="body2" sx={{ flex: 1 }}>
        Resumed unsaved receiving{when ? ` from ${when}` : ""}
      </Typography>
      <Button
        size="small"
        variant="text"
        sx={{ color: "primary.main", fontWeight: 700, textTransform: "none" }}
        onClick={onDiscard}
      >
        DISCARD
      </Button>
      <IconButton
        size="small"
        aria-label="Dismiss"
        onClick={onDismiss}
        sx={{ color: "#fff" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

/**
 * Inventory Receiving — scan / enter items against an order
 */
export default function Receiving() {
  const { user } = useAuth();
  const { location: globalLocation } = useContext(Context);

  const effectiveLocationId =
    globalLocation?.location_id && globalLocation.location_id !== -1
      ? globalLocation.location_id
      : user.location_id;
  const locationName =
    (globalLocation?.location_id && globalLocation.location_id !== -1
      ? globalLocation.location_name
      : user.location_name) || "";

  const barcodeRef = useRef(null);
  const [items, setItems] = useState([]);
  const [orderNumber, setOrderNumber] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [qtyReceived, setQtyReceived] = useState(1);
  const [barcode, setBarcode] = useState("");
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [itemToBe, setItemToBe] = useState(null);
  const [newProductName, setNewProductName] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [manufacturerInput, setManufacturerInput] = useState("");
  const [catalogProduct, setCatalogProduct] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [manufacturerSuggestions, setManufacturerSuggestions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [manufacturerSearchLoading, setManufacturerSearchLoading] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Draft persistence. The draft lives under one key per user + location.
  const userId = user?.userID ?? null;
  const draftScope = `${userId}:${effectiveLocationId}`;
  // Autosave only runs once the draft for the *current* scope has been
  // restored; otherwise a location switch would write the old location's
  // items under the new location's key before the restore effect ran.
  const [restoredScope, setRestoredScope] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const { registerGuard } = useUnsavedWork();
  // Content of the draft as restored, while the resume toast is up. Once the
  // user changes anything the toast goes: its DISCARD means "drop what was
  // restored", not "drop what I just scanned".
  const restoredContentRef = useRef(null);

  const resetForm = useCallback(() => {
    setItems([]);
    setQtyReceived(1);
    setBarcode("");
    setOrderNumber("");
    setVendorName("");
    barcodeRef.current?.focus();
  }, []);

  const discardDraft = useCallback(() => {
    clearReceivingDraft(userId, effectiveLocationId);
    resetForm();
  }, [effectiveLocationId, resetForm, userId]);

  // Restore the draft for this user + location on mount and on location change.
  useEffect(() => {
    const draft = loadReceivingDraft(userId, effectiveLocationId);
    restoredContentRef.current = draft ? draftContentKey(draft) : null;
    if (draft) {
      setItems(draft.items);
      setOrderNumber(draft.orderNumber);
      setVendorName(draft.vendorName);
      toast.custom(
        (t) => (
          <ResumeDraftToast
            savedAt={draft.savedAt}
            onDiscard={() => {
              toast.dismiss(t.id);
              discardDraft();
            }}
            onDismiss={() => toast.dismiss(t.id)}
          />
        ),
        { id: "receiving-draft-resumed", duration: 8000 }
      );
    } else {
      setItems([]);
      setOrderNumber("");
      setVendorName("");
    }
    setRestoredScope(draftScope);
    // The toast's DISCARD is bound to this scope; never let it outlive a
    // location switch (or the page), where it would discard the wrong draft.
    return () => toast.dismiss("receiving-draft-resumed");
  }, [discardDraft, draftScope, effectiveLocationId, userId]);

  // Autosave on every change. Writes nothing (and removes the key) when the
  // page is empty, so clearing everything by hand also drops the draft.
  useEffect(() => {
    if (restoredScope !== draftScope) return;
    const draft = { items, orderNumber, vendorName };
    if (
      restoredContentRef.current !== null &&
      restoredContentRef.current !== draftContentKey(draft)
    ) {
      restoredContentRef.current = null;
      toast.dismiss("receiving-draft-resumed");
    }
    saveReceivingDraft(userId, effectiveLocationId, draft);
  }, [
    draftScope,
    effectiveLocationId,
    items,
    orderNumber,
    restoredScope,
    userId,
    vendorName,
  ]);

  // Let the navigation guard ask "is there unsaved work?" and discard it on
  // "Leave and lose changes". Refs keep the registration stable across renders.
  const dirtyRef = useRef(false);
  dirtyRef.current = hasDraftContent({ items, orderNumber, vendorName });
  const discardRef = useRef(discardDraft);
  discardRef.current = discardDraft;
  useEffect(
    () =>
      registerGuard({
        isDirty: () => dirtyRef.current,
        discard: () => discardRef.current(),
      }),
    [registerGuard]
  );

  useEffect(() => {
    const handleKeyPress = (event) => {
      // Enter inside the new-product dialog picks a catalog suggestion.
      if (isPopupVisible) return;
      if (event.key === "Enter" && barcodeRef.current) {
        barcodeRef.current.focus();
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isPopupVisible]);

  useEffect(() => {
    (async () => {
      try {
        setDataLoaded(false);
        const resManufacturers = await baseAxios.get(
          `/products/manufacturers/all`
        );
        setManufacturers(sortedManufacturers(resManufacturers.data));
        setDataLoaded(true);
      } catch {
        toast.error("Error loading data", { duration: 4000 });
        setDataLoaded(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isPopupVisible) return undefined;
    const q = newProductName.trim();
    if (!q) {
      setProductSuggestions([]);
      setProductSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setProductSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await baseAxios.get("/products/catalog-suggest", {
          params: { q, field: "product" },
        });
        if (!cancelled) setProductSuggestions(res.data?.products || []);
      } catch {
        if (!cancelled) setProductSuggestions([]);
      } finally {
        if (!cancelled) setProductSearchLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isPopupVisible, newProductName]);

  useEffect(() => {
    if (!isPopupVisible) return undefined;
    const q = manufacturerInput.trim();
    if (!q) {
      setManufacturerSuggestions([]);
      setManufacturerSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setManufacturerSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await baseAxios.get("/products/catalog-suggest", {
          params: { q, field: "manufacturer" },
        });
        if (!cancelled) {
          setManufacturerSuggestions(res.data?.manufacturers || []);
        }
      } catch {
        if (!cancelled) setManufacturerSuggestions([]);
      } finally {
        if (!cancelled) setManufacturerSearchLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isPopupVisible, manufacturerInput]);

  // New manufacturers typed for earlier lines are offered again, so a second
  // product from the same new manufacturer does not need it retyped.
  const pendingManufacturerNames = useMemo(
    () =>
      items
        .map((item) => item.product?.manufacturer_name)
        .filter((name) => isNewManufacturer(name, manufacturers)),
    [items, manufacturers]
  );

  const manufacturerOptions = useMemo(
    () =>
      manufacturerPickerRows({
        orgManufacturers: manufacturers,
        suggestions: manufacturerSuggestions,
        pendingNames: pendingManufacturerNames,
        selected: selectedManufacturer,
      }),
    [
      manufacturerSuggestions,
      manufacturers,
      pendingManufacturerNames,
      selectedManufacturer,
    ]
  );

  const applyCatalogProduct = useCallback(
    (option) => {
      const name = String(option?.product_name ?? "").trim();
      setCatalogProduct(option);
      setNewProductName(name);
      const mfrName = String(option?.manufacturer_name ?? "").trim();
      if (!mfrName) return;
      const known = manufacturers.find((row) =>
        namesMatch(row.manufacturer_name, mfrName)
      );
      setSelectedManufacturer({
        manufacturer_id: known?.manufacturer_id ?? null,
        manufacturer_name: known?.manufacturer_name || mfrName,
      });
      setManufacturerInput(known?.manufacturer_name || mfrName);
    },
    [manufacturers]
  );

  const addNewItem = useCallback(
    async (scannedCode) => {
      const code = scannedCode || barcode;
      if (!code) return;
      try {
        const resProducts = await baseAxios.get(
          `/inventory/${effectiveLocationId}/${code}`
        );
        const product = resProducts.data;
        if (product) {
          setItems((prev) => [
            {
              product_upc_code: code,
              product,
              qtyReceived,
              note: "",
            },
            ...prev,
          ]);
        } else {
          setIsPopupVisible(true);
          setItemToBe({
            product_upc_code: code,
            qtyReceived,
            note: "New Product",
          });
        }
      } catch (e) {
        if (e?.response?.status === 404 || e?.status === 404) {
          setIsPopupVisible(true);
          setItemToBe({
            product_upc_code: code,
            qtyReceived,
            note: "New Product",
          });
        } else if (e?.message) {
          toast.error(e.message, { duration: 3000 });
        } else {
          toast.error("Error scanning the product, please try again", {
            duration: 3000,
          });
        }
      }
    },
    [barcode, effectiveLocationId, qtyReceived]
  );

  const modifyItem = useCallback(
    (itemId) => {
      setItems((prev) => {
        const existing = prev[itemId];
        if (!existing) return prev;
        const next = prev.filter((_, i) => i !== itemId);
        return [
          {
            product_upc_code: existing.product_upc_code,
            product: existing.product,
            qtyReceived: Number(qtyReceived) + Number(existing.qtyReceived),
            note: existing.note,
          },
          ...next,
        ];
      });
    },
    [qtyReceived]
  );

  const onScan = useCallback(
    async (e) => {
      const code = e.target.value;
      if (e.key === "Enter" && code) {
        e.preventDefault();
        const itemId = items.findIndex(
          (item) => item.product_upc_code === code
        );
        if (itemId !== -1) {
          modifyItem(itemId);
        } else {
          await addNewItem(code);
        }
        setBarcode("");
        setQtyReceived(1);
      } else {
        setBarcode(code);
      }
    },
    [addNewItem, items, modifyItem]
  );

  const onItemChange = useCallback((itemKey, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.product_upc_code === itemKey
          ? { ...item, qtyReceived: value }
          : item
      )
    );
  }, []);

  const onItemNoteChange = useCallback((itemKey, value) => {
    const sanitized = String(value).replace(/[^a-zA-Z0-9\s]/g, "");
    setItems((prev) =>
      prev.map((item) =>
        item.product_upc_code === itemKey ? { ...item, note: sanitized } : item
      )
    );
  }, []);

  const removeItem = useCallback((itemKey) => {
    setItems((prev) => prev.filter((item) => item.product_upc_code !== itemKey));
  }, []);

  const onComplete = useCallback(async () => {
    if (items.length === 0) {
      toast.error("Please add items to receive", { duration: 2500 });
      return;
    }
    if (!orderNumber.trim()) {
      toast.error("Please input order number", { duration: 2500 });
      return;
    }
    if (!vendorName.trim()) {
      toast.error("Please input vendor name", { duration: 2500 });
      return;
    }

    setSubmitting(true);
    try {
      await baseAxios.post("/inventory/order", {
        order_number: orderNumber,
        order_items: items,
        vendor_name: vendorName,
        source: "QSol",
        location: { location_id: effectiveLocationId },
      });
      toast.success("Receiving completed", { duration: 2500 });
      clearReceivingDraft(userId, effectiveLocationId);
      resetForm();
      // Manufacturers created by this receipt are no longer "(new)".
      baseAxios
        .get(`/products/manufacturers/all`)
        .then((res) => setManufacturers(sortedManufacturers(res.data)))
        .catch(() => {});
    } catch {
      toast.error("An error happened. Please try again.", { duration: 3000 });
    } finally {
      setSubmitting(false);
    }
  }, [effectiveLocationId, items, orderNumber, resetForm, userId, vendorName]);

  // Cancel now also wipes the persisted draft, so it asks first when there is
  // anything to lose. (It also clears the order number, which the old Cancel
  // left in place; a cancelled receiving should start from a clean slate.)
  const onCancel = useCallback(() => {
    if (hasDraftContent({ items, orderNumber, vendorName })) {
      setCancelConfirmOpen(true);
      return;
    }
    resetForm();
  }, [items, orderNumber, resetForm, vendorName]);

  const confirmCancel = useCallback(() => {
    setCancelConfirmOpen(false);
    discardDraft();
  }, [discardDraft]);

  const handleCancelPopup = useCallback(() => {
    setIsPopupVisible(false);
    setNewProductName("");
    setSelectedManufacturer(null);
    setManufacturerInput("");
    setCatalogProduct(null);
    setProductSuggestions([]);
    setManufacturerSuggestions([]);
    setItemToBe(null);
  }, []);

  const handleConfirm = useCallback(() => {
    const productName = newProductName.trim();
    if (!productName) {
      toast.error("Please enter a product name", { duration: 2500 });
      return;
    }
    const typedManufacturer = manufacturerInput.trim();
    const known = manufacturerOptions.find((row) =>
      namesMatch(row.manufacturer_name, typedManufacturer)
    );
    const manufacturerName = (
      selectedManufacturer &&
      namesMatch(selectedManufacturer.manufacturer_name, typedManufacturer)
        ? selectedManufacturer.manufacturer_name
        : known?.manufacturer_name || typedManufacturer
    ).trim();
    if (!manufacturerName) {
      toast.error("Please select a manufacturer", { duration: 2500 });
      return;
    }
    // Only an organization row has an id worth sending; a new name (typed or
    // from the JD Tech catalog) is created by the API on Complete.
    const manufacturerId =
      manufacturers.find((row) =>
        namesMatch(row.manufacturer_name, manufacturerName)
      )?.manufacturer_id ?? null;
    const detail = catalogDetailForRow(
      catalogProduct,
      productName,
      manufacturerName
    );
    setItems((prev) => [
      {
        ...itemToBe,
        product: {
          product_name: productName,
          manufacturer_id: manufacturerId,
          manufacturer_name: manufacturerName,
          ...detail,
        },
      },
      ...prev,
    ]);
    handleCancelPopup();
  }, [
    catalogProduct,
    handleCancelPopup,
    itemToBe,
    manufacturerInput,
    manufacturerOptions,
    manufacturers,
    newProductName,
    selectedManufacturer,
  ]);

  const noopPagination = useCallback(() => {}, []);

  const columns = useMemo(
    () => [
      {
        id: "product_upc_code",
        label: "Product Barcode",
        field: "product_upc_code",
        width: "11%",
        render: (v) => textCell(v),
      },
      {
        id: "manufacturer",
        label: "Manufacturer",
        field: "manufacturer",
        width: "11%",
        sortable: false,
        render: (_, row) => {
          const name = row.product?.manufacturer_name;
          return isNewManufacturer(name, manufacturers)
            ? textCell(`${String(name).trim()} (new)`)
            : textCell(name);
        },
      },
      {
        id: "brand",
        label: "Brand",
        field: "brand",
        width: "9%",
        sortable: false,
        render: (_, row) => textCell(row.product?.brand_name),
      },
      {
        id: "category",
        label: "Category",
        field: "category",
        width: "9%",
        sortable: false,
        render: (_, row) => textCell(row.product?.category_name),
      },
      {
        id: "type",
        label: "Type",
        field: "type",
        width: "8%",
        sortable: false,
        render: (_, row) => textCell(row.product?.product_type_name),
      },
      {
        id: "name",
        label: "Name",
        field: "name",
        width: "16%",
        sortable: false,
        render: (_, row) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {textCell(row.product?.product_name)}
            </Box>
            {row.product?.catalog_source === "master" ? (
              <Chip
                size="small"
                label="JD Tech catalog"
                title="Not in your catalog yet. It will be added when you complete receiving."
                sx={{
                  bgcolor: colors.gold,
                  color: colors.black,
                  fontWeight: 600,
                  fontSize: 10,
                  height: 20,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
          </Box>
        ),
      },
      {
        id: "qty_on_hand",
        label: "Qty on Hand",
        field: "qty_on_hand",
        width: "8%",
        sortable: false,
        render: (_, row) =>
          formatQty(row.product?.inventory_current_quantity),
      },
      {
        id: "qtyReceived",
        label: "Qty Received",
        field: "qtyReceived",
        width: "10%",
        render: (_, row) => (
          <TextField
            size="small"
            type="number"
            value={row.qtyReceived ?? ""}
            onChange={(e) =>
              onItemChange(row.product_upc_code, e.target.value)
            }
            onClick={(e) => e.stopPropagation()}
            inputProps={{ min: 0, step: "any" }}
            sx={compactFieldSx}
          />
        ),
      },
      {
        id: "note",
        label: "Note",
        field: "note",
        width: "12%",
        sortable: false,
        render: (_, row) => (
          <TextField
            size="small"
            value={row.note ?? ""}
            onChange={(e) =>
              onItemNoteChange(row.product_upc_code, e.target.value)
            }
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              minWidth: 0,
              "& .MuiOutlinedInput-root": {
                borderRadius: CONTROL_RADIUS,
                fontSize: 13,
                height: 28,
                minWidth: 0,
              },
              "& .MuiOutlinedInput-input": {
                py: 0.25,
                px: 0.75,
                minWidth: 0,
              },
            }}
          />
        ),
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        width: "6%",
        render: (_, row) => (
          <IconButton
            size="small"
            aria-label="Remove item"
            onClick={(e) => {
              e.stopPropagation();
              removeItem(row.product_upc_code);
            }}
            sx={{
              color: "text.secondary",
              borderRadius: CONTROL_RADIUS,
              "&:hover": { color: "error.main", bgcolor: "error.50" },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [manufacturers, onItemChange, onItemNoteChange, removeItem]
  );

  if (!dataLoaded) {
    return (
      <Box
        sx={{
          px: 8,
          py: 6,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: colors.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 8, py: 3, width: "100%", minWidth: 0, overflowX: "hidden" }}>
      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        // The dialog's focus trap swallows a focus() made while it is open, so
        // the barcode field is focused once the dialog has fully closed.
        disableRestoreFocus
        TransitionProps={{ onExited: () => barcodeRef.current?.focus() }}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{
            fontWeight: 700,
            fontSize: "1.125rem",
            lineHeight: 1.3,
            letterSpacing: 0,
          }}
        >
          Discard this receiving?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The scanned items, order number and vendor name will be removed.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setCancelConfirmOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Keep editing
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmCancel}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: CONTROL_RADIUS,
              boxShadow: "none",
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isPopupVisible}
        onClose={handleCancelPopup}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: colors.black,
            color: colors.white,
            fontWeight: 700,
            fontSize: "1.125rem",
            lineHeight: 1.3,
            letterSpacing: 0,
            py: 1.5,
          }}
        >
          New product
          <IconButton
            aria-label="close"
            onClick={handleCancelPopup}
            size="small"
            sx={{
              color: colors.gold,
              "&:hover": { bgcolor: "rgba(197,160,111,0.15)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={productSuggestions}
              loading={productSearchLoading}
              value={newProductName}
              inputValue={newProductName}
              onChange={(_event, newValue) => {
                if (newValue && typeof newValue === "object") {
                  applyCatalogProduct(newValue);
                  return;
                }
                setCatalogProduct(null);
                setNewProductName(
                  typeof newValue === "string" ? newValue : ""
                );
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === "reset") {
                  setNewProductName(value);
                  return;
                }
                if (reason === "input" || reason === "clear") {
                  setNewProductName(value);
                  setCatalogProduct((current) =>
                    current && namesMatch(current.product_name, value)
                      ? current
                      : null
                  );
                }
              }}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : String(option.product_name ?? "").trim()
              }
              isOptionEqualToValue={(option, value) =>
                option.product_id === value.product_id
              }
              renderOption={({ key, ...props }, option) => (
                <li key={key} {...props}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontSize={14}>{option.product_name}</Typography>
                    {option.manufacturer_name ? (
                      <Typography variant="caption" color="text.secondary">
                        {option.manufacturer_name}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Product name"
                  sx={fieldSx}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                    "aria-label": "Product name",
                  }}
                />
              )}
            />
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={manufacturerOptions}
              filterOptions={manufacturerFilterOptions}
              loading={manufacturerSearchLoading}
              value={selectedManufacturer}
              inputValue={manufacturerInput}
              onChange={(_event, newValue) => {
                const name =
                  typeof newValue === "string"
                    ? newValue.trim()
                    : String(newValue?.manufacturer_name ?? "").trim();
                if (!name) {
                  setSelectedManufacturer(null);
                  return;
                }
                const known = manufacturers.find((row) =>
                  namesMatch(row.manufacturer_name, name)
                );
                setSelectedManufacturer(
                  known || {
                    manufacturer_id: null,
                    manufacturer_name: name,
                    pending: true,
                  }
                );
                setManufacturerInput(known?.manufacturer_name || name);
              }}
              onInputChange={(_event, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setManufacturerInput(value);
                  setSelectedManufacturer((current) =>
                    current && namesMatch(current.manufacturer_name, value)
                      ? current
                      : null
                  );
                } else if (reason === "reset") {
                  setManufacturerInput(value);
                }
              }}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? option
                  : String(option.manufacturer_name ?? "").trim()
              }
              isOptionEqualToValue={(option, value) =>
                namesMatch(option.manufacturer_name, value?.manufacturer_name)
              }
              renderOption={({ key, ...props }, option) => (
                <li key={key} {...props}>
                  {optionLabel(option, "manufacturer_name")}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Manufacturer (pick one or type a new name)"
                  helperText={
                    isNewManufacturer(manufacturerInput, manufacturers)
                      ? `"${manufacturerInput.trim()}" (new) will be added to your manufacturers when you complete receiving.`
                      : undefined
                  }
                  sx={fieldSx}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "off",
                    "aria-label": "Manufacturer",
                  }}
                />
              )}
            />
            {itemToBe?.product_upc_code ? (
              <Typography variant="body2" color="text.secondary">
                Barcode: {itemToBe.product_upc_code}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleCancelPopup}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: CONTROL_RADIUS,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Inventory Receiving
            </Typography>
            {locationName ? (
              <Chip
                size="small"
                label={locationName}
                sx={{
                  bgcolor: colors.black,
                  color: colors.gold,
                  fontWeight: 600,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
            {items.length > 0 ? (
              <Chip
                size="small"
                label={`${items.length} item${items.length === 1 ? "" : "s"}`}
                sx={{
                  bgcolor: colors.gold,
                  color: colors.black,
                  fontWeight: 600,
                  borderRadius: CONTROL_RADIUS,
                }}
              />
            ) : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            Scan barcodes to receive inventory against an order.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={onCancel}
            disabled={submitting}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: CONTROL_RADIUS,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onComplete}
            disabled={submitting}
            sx={{
              minHeight: 40,
              px: 2.5,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: CONTROL_RADIUS,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {submitting ? "Saving…" : "Complete"}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          label="Order Number"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          sx={{ minWidth: 200, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Vendor Name"
          value={vendorName}
          onChange={(e) => setVendorName(e.target.value)}
          sx={{ minWidth: 220, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Barcode"
          inputRef={barcodeRef}
          autoFocus
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyDown={onScan}
          placeholder="Scan or type, then Enter"
          sx={{ minWidth: 240, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Qty Received"
          type="number"
          value={qtyReceived}
          onChange={(e) => setQtyReceived(e.target.value)}
          inputProps={{ min: 0, step: "any" }}
          sx={{ width: 130, ...fieldSx }}
        />
      </Box>

      <PaginatedTable
        columns={columns}
        rows={items}
        totalRowCount={items.length}
        loading={false}
        onPaginationChange={noopPagination}
        clientSidePagination
        skeletonHeight="28px"
        getRowKey={(row, index) =>
          `${row.product_upc_code || "item"}-${index}`
        }
        fitWidth
        emptyMessage="Scan a barcode to add received items"
      />
    </Box>
  );
}
